import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ExtensionAppActions from './extension-app.actions';
import { EMPTY, catchError, exhaustMap, filter, map, of, switchMap, take, tap } from 'rxjs';
import { AuthServiceFacade } from '@pcp/auth';
import { IntegratedAppComponentType } from '@pcp/core/interfaces';
import { ExtensionAppApiService } from '../services/extension-app-api.service';
import { ClassroomFacade } from 'shared/classroom/data-access';

const mapToOpenSuccessOrDenied = (hasAccess: boolean, extensionApp: IntegratedAppComponentType) =>
    hasAccess
        ? ExtensionAppActions.openExtensionAppSuccess({ extensionApp })
        : ExtensionAppActions.openExtensionAppDenied({ extensionApp });

@Injectable()
export class ExtensionAppEmitterEffects {
    private readonly actions$ = inject(Actions);
    private readonly auth = inject(AuthServiceFacade);
    private readonly extensionAppApi = inject(ExtensionAppApiService);
    private readonly lessonYjs = inject(ClassroomFacade);

    readonly openPushTheButton$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExtensionAppActions.requestOpenExtensionApp),
            filter(action => action.extensionApp === 'PushTheButton'),
            switchMap(action =>
                this.auth.canOpenPushTheButton$.pipe(
                    take(1),
                    map(hasAccess => mapToOpenSuccessOrDenied(hasAccess, action.extensionApp)),
                ),
            ),
        ),
    );

    readonly openPracticeTrainer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExtensionAppActions.requestOpenExtensionApp),
            filter(action => action.extensionApp === 'PracticeTrainer'),
            switchMap(action =>
                this.auth.canOpenPracticeTrainer$.pipe(
                    take(1),
                    map(hasAccess => mapToOpenSuccessOrDenied(hasAccess, action.extensionApp)),
                ),
            ),
        ),
    );

    /** Write open extension app to Yjs (replaces SignalR broadcast) */
    readonly sendOpenExtensionApp$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ExtensionAppActions.openExtensionAppSuccess),
                filter(
                    (action): action is { extensionApp: NonNullable<IntegratedAppComponentType> } & typeof action =>
                        action.extensionApp !== null,
                ),
                tap(({ extensionApp }) => this.lessonYjs.openExtensionApp(extensionApp)),
            ),
        { dispatch: false },
    );

    /** Write close extension app to Yjs + stop PTB API if it was open (replaces SignalR broadcast) */
    readonly closeExtensionApp$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ExtensionAppActions.closeExtensionApp),
                switchMap(() => {
                    const openedApp = this.lessonYjs.getSnapshot()?.activeExtensionApp ?? null;
                    return openedApp === 'PushTheButton'
                        ? this.extensionAppApi.stopPushTheButton().pipe(catchError(() => of(null)))
                        : of(null);
                }),
                tap(() => this.lessonYjs.closeExtensionApp()),
            ),
        { dispatch: false },
    );

    /** Start PushTheButton REST API when PTB is opened (teacher-only, no console.log) */
    readonly openExtensionAppSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ExtensionAppActions.openExtensionAppSuccess),
                filter(action => action.extensionApp === 'PushTheButton'),
                exhaustMap(() => this.extensionAppApi.startPushTheButton().pipe(catchError(() => EMPTY))),
            ),
        { dispatch: false },
    );
}
